/**
 * 台本取り込みの入口 (アイコンのみ。ツールチップ「シナリオファイルをインポート」) (docs/features/scenario-script-import-plan.md §4.1 / §7)。
 *
 * 台本取り込みは一部のユースケース向けなので、入口は控えめに置く。
 * - 未読み込み (ScenarioScriptImportEntry): シナリオ設定1行目の右上に固定したアイコンボタン。押すとモーダルでドロップゾーンを出し、
 *   解析 API で抽出テキストに変換して state に持つ
 * - 読み込み後 (ScenarioScriptImportCard): 1行目の上に全幅のカード (「プロモーション対象商品」の選択済み表示と同じ体裁)。
 *   先頭にドキュメントアイコン、ファイル名と形式・文字数、「内容を確認」(マークダウン整形表示) と × (解除)
 * - 映像スタイルがモーション系 (production_engine=motion) のときは入口 / カードを非活性にし、理由をツールチップで出す (Phase 1 は非対応)
 *
 * ファイル本体はどこにも保存しない (解析 API もステートレス)。state はページ再読込で消える。
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileDropZone } from '../../../common/FileDropZone';
import { Tooltip } from '../../../common/Tooltip';
import { useToast } from '../../../../hooks/useToast';
import { getResponseErrorMessage } from '../../../../utils/getErrorMessage';
import { useScenarioSetup } from '../hooks/ScenarioSetupContext';
import { requestParseScriptFile } from '../utils';
import {
  SCRIPT_IMPORT_ACCEPT_EXTENSIONS,
  SCRIPT_IMPORT_ACCEPT_MIME_TYPES,
  SCRIPT_IMPORT_MAX_FILE_BYTES,
  SCRIPT_IMPORT_MOTION_REASON,
  type ImportedScriptState,
} from '../scriptImport';
import styles from './ScenarioScriptImport.module.css';
import genStyles from '../ScenarioGenerator.module.css';

/** 台本内のリンクは設定画面を離れないよう新規タブで開く */
const markdownComponents: Components = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
};

/** Escape で閉じる (busy の間は閉じない) */
function useEscapeToClose(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
}

function ScriptModal({ title, onClose, size, children }: {
  title: string;
  onClose: () => void;
  size: 'md' | 'lg';
  children: ReactNode;
}) {
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal-card modal-card--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** 入口アイコンのツールチップ / aria-label (ラベル文字は画面に出さない) */
const SCRIPT_IMPORT_ENTRY_LABEL = 'シナリオファイルをインポート';

const SOURCE_FORMAT_LABELS: Record<ImportedScriptState['sourceFormat'], string> = {
  docx: 'Word',
  markdown: 'Markdown',
  text: 'テキスト',
};

/** 未読み込み時の入口 (アイコンのみ)。読み込み後は何も描画しない (カードは ScenarioScriptImportCard)。 */
export function ScenarioScriptImportEntry() {
  const { importedScript, setImportedScript, isMotionEngine } = useScenarioSetup();
  const toast = useToast();
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const disabled = !!isMotionEngine;

  const closeUpload = useCallback(() => {
    if (!isParsing) setIsUploadOpen(false);
  }, [isParsing]);
  useEscapeToClose(isUploadOpen, closeUpload);

  const handleFile = useCallback(async (file: File) => {
    if (file.size > SCRIPT_IMPORT_MAX_FILE_BYTES) {
      toast.error('台本ファイルは5MBまでです');
      return;
    }
    setIsParsing(true);
    try {
      const res = await requestParseScriptFile(file);
      const next: ImportedScriptState = {
        text: res.text,
        filename: file.name,
        sourceFormat: res.source_format,
        charCount: res.char_count,
        warnings: Array.isArray(res.warnings) ? res.warnings : [],
      };
      setImportedScript(next);
      setIsUploadOpen(false);
      toast.success('台本を読み込みました');
    } catch (err) {
      toast.error(getResponseErrorMessage(err) || '台本の読み込みに失敗しました');
    } finally {
      setIsParsing(false);
    }
  }, [setImportedScript, toast]);

  const handleReject = useCallback(() => {
    toast.error('台本は.docx/.md/.txtのファイルで読み込めます');
  }, [toast]);

  if (importedScript) return null;

  return (
    <div className={`${styles.entry} ${styles.entryIconMode}`} data-testid="script-import-entry">
      <div className={styles.entryBody}>
        <Tooltip content={disabled ? SCRIPT_IMPORT_MOTION_REASON : SCRIPT_IMPORT_ENTRY_LABEL}>
          {/* アイコンだけのボタン。名前はツールチップ (と aria-label) でだけ出す */}
          <button
            type="button"
            className={styles.link}
            onClick={() => setIsUploadOpen(true)}
            disabled={disabled}
            aria-label={SCRIPT_IMPORT_ENTRY_LABEL}
            data-testid="script-import-link"
            style={disabled ? { pointerEvents: 'none' } : undefined}
          >
            <img src="/export_file.svg" alt="" aria-hidden="true" className={styles.linkIcon} />
          </button>
        </Tooltip>
      </div>

      {isUploadOpen && (
        <ScriptModal title="シナリオファイルをインポート" onClose={closeUpload} size="md">
          <div className="modal-body">
            <FileDropZone
              data-testid="script-import-dropzone"
              acceptExtensions={SCRIPT_IMPORT_ACCEPT_EXTENSIONS}
              acceptMimeTypes={SCRIPT_IMPORT_ACCEPT_MIME_TYPES}
              onFileSelect={(file) => { void handleFile(file); }}
              onReject={handleReject}
              busy={isParsing}
              busyLabel="台本を読み込み中…"
              disabled={disabled}
              label="台本ファイルをドラッグ＆ドロップ"
            />
            <div className={`${styles.muted} ${styles.note}`}>
              対応形式は.docx/.md/.txtです。セリフ・テロップ・カット割り・尺を台本どおりにシナリオ化します
            </div>
          </div>
          <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn-cancel" onClick={closeUpload} disabled={isParsing}>キャンセル</button>
          </div>
        </ScriptModal>
      )}
    </div>
  );
}

/** 読み込み後の全幅カード (プロモーション対象商品の選択済み表示と同じ体裁)。未読み込みなら何も描画しない。 */
export function ScenarioScriptImportCard() {
  const { importedScript, setImportedScript, isMotionEngine } = useScenarioSetup();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const disabled = !!isMotionEngine;
  const closePreview = useCallback(() => setIsPreviewOpen(false), []);
  useEscapeToClose(isPreviewOpen, closePreview);

  if (!importedScript) return null;

  const meta = [
    'シナリオファイル',
    SOURCE_FORMAT_LABELS[importedScript.sourceFormat] ?? importedScript.sourceFormat,
    `${importedScript.charCount.toLocaleString()}字`,
  ].join('・');

  const card = (
    <div
      className={`${genStyles.productBox} ${styles.card} ${disabled ? styles.cardDimmed : ''}`}
      data-testid="script-import-card"
    >
      {/* 先頭はドキュメントのアイコン (商品カードの画像枠と同じ寸法) */}
      <span className={styles.cardIcon} aria-hidden="true">
        <img src="/design_file_text_image.svg" alt="" />
      </span>
      <div className={genStyles.productBoxInfo}>
        <span className={genStyles.productBoxTitle} title={importedScript.filename}>{importedScript.filename}</span>
        <span className={genStyles.productBoxMeta}>{meta}</span>
        {importedScript.warnings.length > 0 && (
          <ul className={styles.cardWarnings} data-testid="script-import-card-warnings">
            {importedScript.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        )}
      </div>
      <div className={`${genStyles.productBoxActions} ${styles.cardActions}`}>
        <button
          type="button"
          className="btn-modal-secondary-sm"
          onClick={() => setIsPreviewOpen(true)}
          disabled={disabled}
          aria-label={`台本の内容を確認: ${importedScript.filename}`}
          style={disabled ? { pointerEvents: 'none' } : undefined}
        >
          内容を確認
        </button>
        {/* モーション系でも解除はできるようにしておく (読み込みを持ち越さず別の映像スタイルで使い直せる) */}
        <button
          type="button"
          className={genStyles.productBoxRemove}
          onClick={() => setImportedScript(null)}
          aria-label="台本を解除"
        >
          ✕
        </button>
      </div>
    </div>
  );

  return (
    <>
      {disabled ? <Tooltip content={SCRIPT_IMPORT_MOTION_REASON}>{card}</Tooltip> : card}

      {isPreviewOpen && (
        <ScriptModal title="台本の内容" onClose={closePreview} size="lg">
          <div className="modal-body">
            <div className={styles.muted} style={{ marginBottom: 8 }}>
              {importedScript.filename} / {importedScript.charCount.toLocaleString()}字（読み取り専用）
            </div>
            {importedScript.warnings.length > 0 && (
              <ul className={styles.warnings} data-testid="script-import-preview-warnings">
                {importedScript.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
            <div className={styles.markdown} data-testid="script-import-preview-text">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
                disallowedElements={['img']}
                unwrapDisallowed
              >
                {importedScript.text}
              </ReactMarkdown>
            </div>
          </div>
        </ScriptModal>
      )}
    </>
  );
}
